import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Slider,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import PopoverPicker from "./PopoverColorPicker";

const PREFIX = "EyeStylePicker";

const classes = {
  popoverContainer: `${PREFIX}-popoverContainer`,
  slidersContainer: `${PREFIX}-slidersContainer`,
  slidersStyle: `${PREFIX}-slidersStyle`,
  qrAccordionDetails: `${PREFIX}-qrAcordionDetails`,
};

const EyeStylePickerContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.popoverContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "1rem",
      width: "50%",
    },
    [`& .${classes.slidersContainer}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.slidersStyle}`]: {
      width: "70%",
    },
  };
});

const EyeStylePicker = ({ qrCodeParams, updateQrParams, setQrCodeParams }) => {
  const { t } = useTranslation();

  const saveEyeColor = (index, key, value) => {
    const tmp = { ...qrCodeParams.qrParams };
    const myColors = [...tmp.eyeColor];
    myColors[index] = { ...myColors[index], [key]: value };
    tmp.eyeColor = myColors;
    setQrCodeParams({ ...qrCodeParams, qrParams: tmp });
  };

  return (
    <EyeStylePickerContainer>
      <div>
        <div>
          <Accordion className={classes.qrAccordionStyle}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="qr-eyes-radius-controlNW"
              id="qr-eyes-radius-headerNW"
            >
              <Typography>{t("QrManager.northWest")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorOuter")}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[0].outer}
                  onChange={(e) => saveEyeColor(0, "outer", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[0].outer}
                  onChangeHex={(e) => saveEyeColor(0, "outer", e)}
                />
              </div>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorInner")}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[0].inner}
                  onChange={(e) => saveEyeColor(0, "inner", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[0].inner}
                  onChangeHex={(e) => saveEyeColor(0, "inner", e)}
                />
              </div>
              <div className={classes.slidersContainer}>
                <Typography variant="body1">
                  {t("QrManager.northWest")}
                </Typography>
                <Slider
                  aria-label="north-west-angle-1"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopLeftShape[0]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopLeftShape",
                      0
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.northEast")}
                </Typography>
                <Slider
                  aria-label="north-west-angle-2"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopLeftShape[1]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopLeftShape",
                      1
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southWest")}
                </Typography>
                <Slider
                  aria-label="north-west-angle-3"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopLeftShape[2]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopLeftShape",
                      2
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southEast")}
                </Typography>
                <Slider
                  aria-label="north-west-angle-4"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopLeftShape[3]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopLeftShape",
                      3
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion className={classes.qrAccordionStyle}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="qr-eyes-radius-controlNE"
              id="qr-eyes-radius-headerNE"
            >
              <Typography>{t("QrManager.northEast")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorOuter") + "lalalalal"}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[1].outer}
                  onChange={(e) => saveEyeColor(1, "outer", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[1].outer}
                  onChangeHex={(e) => saveEyeColor(1, "outer", e)}
                />
              </div>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorInner")}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[1].inner}
                  onChange={(e) => saveEyeColor(1, "inner", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[1].inner}
                  onChangeHex={(e) => saveEyeColor(1, "inner", e)}
                />
              </div>
              <div className={classes.slidersContainer}>
                <Typography variant="body1">
                  {t("QrManager.northWest")}
                </Typography>
                <Slider
                  aria-label="north-east-angle-1"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopRightShape[0]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopRightShape",
                      0
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.northEast")}
                </Typography>
                <Slider
                  aria-label="north-east-angle-2"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopRightShape[1]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopRightShape",
                      1
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southWest")}
                </Typography>
                <Slider
                  aria-label="north-east-angle-3"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopRightShape[2]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopRightShape",
                      2
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southEast")}
                </Typography>
                <Slider
                  aria-label="north-east-angle-4"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeTopRightShape[3]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeTopRightShape",
                      3
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="qr-eyes-radius-controlSW"
              id="qr-eyes-radius-headerSW"
            >
              <Typography>{t("QrManager.southWest")}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.lastAccordionMargin}>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorOuter")}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[2].outer}
                  onChange={(e) => saveEyeColor(2, "outer", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[2].outer}
                  onChangeHex={(e) => saveEyeColor(2, "outer", e)}
                />
              </div>
              <div className={classes.popoverContainer}>
                <Typography variant="body1">
                  {t("QrManager.eyeColorInner")}
                </Typography>
                <PopoverPicker
                  color={qrCodeParams.qrParams.eyeColor[2].inner}
                  onChange={(e) => saveEyeColor(2, "inner", e)}
                  hexColor={qrCodeParams.qrParams.eyeColor[2].inner}
                  onChangeHex={(e) => saveEyeColor(2, "inner", e)}
                />
              </div>
              <div className={classes.slidersContainer}>
                <Typography variant="body1">
                  {t("QrManager.northWest")}
                </Typography>
                <Slider
                  aria-label="south-west-angle-1"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeBottomLeftShape[0]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeBottomLeftShape",
                      0
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.northEast")}
                </Typography>
                <Slider
                  aria-label="south-west-angle-2"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeBottomLeftShape[1]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeBottomLeftShape",
                      1
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southWest")}
                </Typography>
                <Slider
                  aria-label="south-west-angle-3"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeBottomLeftShape[2]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeBottomLeftShape",
                      2
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
                <Typography variant="body1">
                  {t("QrManager.southEast")}
                </Typography>
                <Slider
                  aria-label="south-west-angle-4"
                  size="small"
                  className={classes.slidersStyle}
                  value={qrCodeParams.qrParams.eyeBottomLeftShape[3]}
                  onChange={(e) => {
                    updateQrParams(
                      "qrParams",
                      e.target.value,
                      "eyeBottomLeftShape",
                      3
                    );
                  }}
                  getAriaValueText={(value) => `${value}°`}
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={20}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </EyeStylePickerContainer>
  );
};

export default EyeStylePicker;
